import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { connect } from "react-redux";
import { compose, graphql } from "lib/apolloClient";
import classNames from "classnames";
import _ from "lodash";
import withDerivedProps from "lib/withDerivedProps";

// DS
import {
  DSProvider,
  Modal,
  ToggleSwitch,
  SearchInput,
  Dropdown,
  ButtonDisclosure,
  Badge,
  SideNavigation,
  Button,
  EmptyState,
} from "@toddle-edu/ds-web";
import { FilterOutlined } from "@toddle-edu/ds-icons";
import { NoDataIllustration } from "@toddle-edu/ds-theme";

// components
import Loading from "UIComponents/Loading";
import FiltersOverlay from "UIComponents/FiltersOverlay";
import StandardsTable from "./components/StandardsTable";
import DiscardLearningGoalsModal from "UnitPlans/components/IBcomponents/AddLearningGoals/components/DiscardLearningGoalsModal";

// queries
import {
  getStandardSetsQuery,
  getOrganizationSubjectsGradesAndTagsQuery,
} from "./modules/Queries";

// modules
import {
  bulkCreateStandardRubricCriteria,
  removeStandardRubricCriterias,
} from "AppComponents/StandardRubric/modules/StandardRubricModule";
import { useDispatch } from "Hooks/useDispatch";

// utils
import { useI18n } from "Hooks";
import getCurriculumIdsFilterForTags from "Utils/getCurriculumIdsFilterForTags";
import { getDatasetsOrderByArgs } from "Datasets/modules/Utils";
import { getOrganizationId } from "modules/CommonSelectors";
import {
  getFilterOverlayOptions,
  getAllFiltersCount,
  getAppliedFiltersCountForFilter,
  getSideNavigationOptions,
} from "./modules/Utils";
import {
  getFieldValue,
  getResolvedMinimalTreeSet,
  FIELD_KEY_TO_PLANNER_ELEMENT_TYPE,
} from "Assessments/modules/Utils";
import {
  getIsSubjectBasedGradingEnabled,
  getIsLearningCoursesEnabled,
} from "Teacher/routes/LearningCourses/modules/Utils";

// constants
import { DEFAULT_STANDARD_FILTERS } from "./modules/Constants";
import { CURRICULUM_TYPE_PYP } from "Constants/stringConstants";

// scss
import classes from "./LearningGoalsStandardsTagModal.scss";
import { getIsNewGradingSetupEnabled } from "ClassRoom/modules/utils";

const styles = {
  modalHeaderStyle: { marginBottom: 0 },
  bodyContainer: {
    display: "flex",
    flexDirection: "row",
    padding: "0",
    overflow: "hidden",
  },
  sideNavigationContainerStyle: {
    height: "100%",
    flex: "0 0 auto",
  },
};

const EMPTY_ARRAY = [];

const getDiscardModalObj = ({
  t,
  totalSelected,
  newSelectionsCount,
  assessmentTypeTitle,
}) => {
  return {
    add: {
      headerTitle: t("classRoom:discard_selected_learning_goals"),
      message: t("classRoom:discard_selected_learning_goals_message", {
        count: totalSelected,
        label: t("common:standard_other"),
        type: assessmentTypeTitle,
      }),
      buttonLabel1: t("common:keep"),
      buttonLabel2: t("common:discard"),
    },
    edit: {
      headerTitle: t("classRoom:discard_changes_to_selected_learning_goals"),
      message: t(
        "classRoom:discard_changes_to_selected_learning_goals_message",
        {
          count: newSelectionsCount,
          label: t("common:standard_other"),
          type: assessmentTypeTitle,
        }
      ),
      buttonLabel1: t("journal:keep_changes"),
      buttonLabel2: t("common:discard_changes"),
    },
  };
};

const LearningGoalsStandardsTagModalBody = props => {
  const {
    isLoading,
    isData,
    learningCourseId,
    grades,
    subjects,
    tags,
    standardSets: globalStandardSets,
    showCourseStandards,
    selectedStandardSet,
    setSelectedStandardSet,
    selectedStandards,
    setSelectedStandards,
    standardsEdgesRef,
    unitPlan,
    showOnlyUnitSelections,
    fieldKey,
    curriculumProgramType,
  } = props;

  // ref
  const { t } = useI18n();

  // state
  const [searchText, setSearchText] = useState("");
  const [standardFilters, setStandardFilters] = useState(
    DEFAULT_STANDARD_FILTERS
  );
  const [isFilterOverlayOpen, setIsFilterOverlayOpen] = useState(false);

  // variables
  const filterOverlayOptions = getFilterOverlayOptions({
    t,
    grades,
    subjects,
    tags,
  });
  const standardSets = useMemo(() => {
    if (!showOnlyUnitSelections) {
      // globalStandardSets has standardSet in case of learning course, node in case of curriculum
      return _(globalStandardSets)
        .map(({ standardSet, node }) => standardSet || node)
        .compact()
        .value();
    }

    // get unit plan data
    const unitPlanFields = _.get(unitPlan, "allFields", []);
    const unitPlanNodes = getResolvedMinimalTreeSet({
      fields: unitPlanFields,
      uid: fieldKey,
      curriculumType: curriculumProgramType,
    });

    // get unique standard sets from unit plan nodes
    const unitPlanStandardSets = _.uniqBy(
      _.map(unitPlanNodes, node => node?.standardsWrapper?.standardSet || {}),
      "id"
    );

    return unitPlanStandardSets;
  }, [
    globalStandardSets,
    unitPlan,
    showOnlyUnitSelections,
    fieldKey,
    curriculumProgramType,
  ]);

  const sideNavigationOptions = useMemo(
    () =>
      getSideNavigationOptions({
        standardSets,
      }),
    [standardSets]
  );

  const appliedFiltersCount = useMemo(
    () => getAllFiltersCount({ currentFilters: standardFilters }),
    [standardFilters]
  );

  // callbacks
  const onApplyFilterOverlay = useCallback(({ filters }) => {
    setStandardFilters(filters);
    setIsFilterOverlayOpen(false);
  }, []);

  const onCloseFilterOverlay = useCallback(
    () => setIsFilterOverlayOpen(false),
    []
  );

  const customResetFilters = useCallback(() => {
    setStandardFilters(DEFAULT_STANDARD_FILTERS);
    setSearchText("");
  }, []);

  const onSelectStandardSet = useCallback(
    standardSetId => {
      setSelectedStandardSet(standardSetId);
    },
    [setSelectedStandardSet]
  );

  // side effects
  useEffect(() => {
    const isSelectedStandardSetValid = _.some(standardSets, {
      id: selectedStandardSet,
    });

    if (
      !_.isEmpty(standardSets) &&
      (!selectedStandardSet || !isSelectedStandardSetValid)
    ) {
      setSelectedStandardSet(_.first(standardSets)?.id || null);
    }
  }, [standardSets, showOnlyUnitSelections]);

  // render functions
  const renderFilters = () => {
    return (
      <div className={classes.contentSpaceBetween}>
        <div className={classes.searchInputContainer}>
          {/* search is common for both steps */}
          <SearchInput
            value={searchText}
            placeholder={t("common:search_for_a_with_label", {
              label: t("common:standard_plural"),
            })}
            size={"medium"}
            onChange={setSearchText}
            focusSearchBySlash={true}
          />
        </div>
        {!_.isEmpty(filterOverlayOptions) ? (
          <Dropdown
            overlay={
              <FiltersOverlay
                options={filterOverlayOptions}
                currentFilters={standardFilters} // state
                getAllFiltersCount={getAllFiltersCount}
                getAppliedFiltersCountForFilter={
                  getAppliedFiltersCountForFilter
                }
                onApply={onApplyFilterOverlay}
                handleClose={onCloseFilterOverlay}
                defaultFilters={DEFAULT_STANDARD_FILTERS}
              />
            }
            visible={isFilterOverlayOpen}
            onVisibleChange={setIsFilterOverlayOpen}
          >
            <ButtonDisclosure
              icon={<FilterOutlined />}
              type="outlined"
              variant="neutral"
            >
              {appliedFiltersCount > 0 ? (
                <span className={classes.filterBtn}>
                  <Badge
                    value={appliedFiltersCount}
                    type="numeric"
                    variant="dark"
                    shape="circle"
                  />
                </span>
              ) : null}
            </ButtonDisclosure>
          </Dropdown>
        ) : null}
      </div>
    );
  };

  const renderBody = () => {
    if (_.isEmpty(standardSets)) {
      return (
        <EmptyState
          illustration={NoDataIllustration}
          title={t("scopeAndSequence:standard_details_empty_state_title")}
        />
      );
    }

    return (
      <StandardsTable
        searchText={searchText}
        selectedStandardSet={selectedStandardSet}
        selectedStandards={selectedStandards}
        setSelectedStandards={setSelectedStandards}
        standardFilters={standardFilters}
        customResetFilters={customResetFilters}
        learningCourseId={learningCourseId}
        showCourseStandards={showCourseStandards}
        standardsEdgesRef={standardsEdgesRef}
        unitPlan={unitPlan}
        showOnlyUnitSelections={showOnlyUnitSelections}
        fieldKey={fieldKey}
      />
    );
  };

  return (
    <div className={classes.bodyContainer}>
      {isLoading && !isData ? (
        <Loading />
      ) : (
        <>
          {!_.isEmpty(standardSets) && _.size(standardSets) > 1 ? (
            <SideNavigation
              options={sideNavigationOptions}
              selectedOptionKey={selectedStandardSet}
              onClick={onSelectStandardSet}
            />
          ) : null}
          <div className={classes.bodyInnerContainer}>
            {renderFilters()}
            {renderBody()}
          </div>
        </>
      )}
    </div>
  );
};

const mapStateToProps = state => {
  const orgId = getOrganizationId();

  const { id: curriculumProgramId, type: curriculumProgramType } =
    _.get(state, "platform.currentCurriculumProgram", {}) || {};

  return {
    orgId,
    curriculumProgramId,
    curriculumProgramType,
    isData: true,
    isLoading: false,
  };
};

const getDerivedProps = props => {
  const { assessmentFields } = props;
  const subjects = _.find(assessmentFields, { uid: "subjects" })?.value;

  const isSubjectBasedGrading =
    !getIsLearningCoursesEnabled() || getIsSubjectBasedGradingEnabled();
  return {
    ...getDatasetsOrderByArgs(),
    isSubjectBasedGrading,
    assessmentSubjects: subjects,
  };
};

const LearningGoalsStandardsTagModalBodyWithQuery = compose(
  connect(mapStateToProps, null),
  withDerivedProps(getDerivedProps),
  graphql(getOrganizationSubjectsGradesAndTagsQuery, {
    name: "getOrganizationSubjectsGradesAndTags",
    options: ({ orgId, curriculumProgramId }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        orgId,
        curriculumProgramId,
        genericTagsFilter: {
          types: ["GENERIC"],
          ...getCurriculumIdsFilterForTags({ curriculumProgramId }),
        },
      },
    }),
    skip: ({ orgId, curriculumProgramId }) => !orgId || !curriculumProgramId,
    props({ getOrganizationSubjectsGradesAndTags: { node, networkStatus } }) {
      const subjects = node?.subjects || EMPTY_ARRAY;
      const grades = node?.grades || EMPTY_ARRAY;
      const tags = node?.genericTagsV2 || EMPTY_ARRAY;

      return {
        subjects,
        grades,
        tags,
        isData: !_.isEmpty(node),
        isLoading: _.includes([1, 2], networkStatus),
      };
    },
  }),
  graphql(getStandardSetsQuery, {
    name: "getStandardSets",
    options: ({
      curriculumProgramId,
      showCourseStandards,
      learningCourseId,
      assessmentSubjects,
      isSubjectBasedGrading,
      curriculumProgramType,
      orderBy,
      orderByDirection,
    }) => {
      const id = showCourseStandards ? learningCourseId : curriculumProgramId;

      const type = showCourseStandards
        ? "LEARNING_COURSE"
        : "CURRICULUM_PROGRAM";

      const filters = {
        archivalState: "ACTIVE",
        subjects: isSubjectBasedGrading ? assessmentSubjects : [],
      };

      return {
        fetchPolicy: "cache-and-network",
        variables: {
          id,
          type,
          orderBy,
          orderByDirection,
          ...(type === "CURRICULUM_PROGRAM"
            ? { plannerElementNodeSetFilters: filters }
            : { plannerElementNodeFilters: filters }),
          isPYPCurriculum: curriculumProgramType
            ? curriculumProgramType === CURRICULUM_TYPE_PYP
            : undefined,
        },
      };
    },
    skip: ({
      curriculumProgramId,
      learningCourseId,
      showCourseStandards,
      isSubjectBasedGrading,
      assessmentSubjects,
    }) => {
      const hasNoId = showCourseStandards
        ? !learningCourseId
        : !curriculumProgramId;

      const hasNoSubjectsForSubjectBasedGrading =
        isSubjectBasedGrading && _.isEmpty(assessmentSubjects);

      return hasNoId || hasNoSubjectsForSubjectBasedGrading;
    },
    props({
      getStandardSets: { node, networkStatus },
      ownProps: { showCourseStandards },
    }) {
      let standardSets = _.get(node, "plannerElementNodeSets.edges", []);

      if (showCourseStandards) {
        standardSets =
          _.get(node, "standardsWrapper.standardsEdge") ||
          _.get(node, "benchmarksWrapper.standardsEdge", []);
      }

      return {
        standardSets,
        isData: !_.isEmpty(node),
        isLoading: _.includes([1, 2], networkStatus),
      };
    },
  })
)(LearningGoalsStandardsTagModalBody);

const LearningGoalsStandardsTagModal = props => {
  const {
    onCloseLearningGoalsModal,
    learningCourseId,
    assessmentFields,
    fieldKey,
    updateInputField,
    hasAddRemoveValues,
    assessmentType,
    isFromQuestion,
    assessmentTools,
    curriculumProgramType,
    plannerElementSets,
  } = props;

  const { t } = useI18n();

  const dispatch = useDispatch();

  const isNewGradingSetupEnabled = getIsNewGradingSetupEnabled();
  const elementType = FIELD_KEY_TO_PLANNER_ELEMENT_TYPE[fieldKey];
  const standardsLabel =
    (isNewGradingSetupEnabled &&
      elementType &&
      _.get(plannerElementSets, `${elementType}.label`)) ||
    t("common:standard_other");

  // refs
  const standardsEdgesRef = useRef([]);

  // state
  const [showCourseStandards, setShowCourseStandards] = useState(
    !_.isEmpty(learningCourseId)
  );
  const [showOnlyUnitSelections, setShowOnlyUnitSelections] = useState(false);
  const [selectedStandardSet, setSelectedStandardSet] = useState(null);
  const [selectedStandards, setSelectedStandards] = useState(
    () =>
      getFieldValue({
        fields: assessmentFields,
        uid: fieldKey,
      }) || []
  );
  const [isDiscardModalVisible, setIsDiscardModalVisible] = useState(false);

  // variables
  const unitPlan = getResolvedMinimalTreeSet({
    fields: assessmentFields,
    uid: "unitPlan",
    curriculumType: curriculumProgramType,
  });
  const totalSelected = useMemo(() => _.size(selectedStandards), [
    selectedStandards,
  ]);
  const newSelectionsCount = useMemo(
    () =>
      _.size(
        _.difference(
          selectedStandards,
          getFieldValue({ fields: assessmentFields, uid: fieldKey }) || []
        )
      ),
    [selectedStandards, assessmentFields, fieldKey]
  );
  const mode = useMemo(
    () =>
      _.size(
        getFieldValue({
          fields: assessmentFields,
          uid: fieldKey,
        }) || []
      ) > 0
        ? "edit"
        : "add",
    [assessmentFields, fieldKey]
  );
  const previousSelectedNodes = getResolvedMinimalTreeSet({
    fields: assessmentFields,
    uid: fieldKey,
    curriculumType: curriculumProgramType,
  });

  const areStandardsSelected = _.size(selectedStandards) > 0;

  const standardsCountClass = classNames("text-label", {
    [classes.standardsCount]: areStandardsSelected,
  });

  const assessmentTypeTitle =
    assessmentType == "le"
      ? t("common:learning_experience")
      : isFromQuestion
      ? t("common:question")
      : t("common:assessment");

  const discardModalObj = useMemo(() => {
    return getDiscardModalObj({
      t,
      totalSelected,
      newSelectionsCount,
      assessmentTypeTitle,
    });
  }, [t, totalSelected, newSelectionsCount, assessmentTypeTitle]);

  // callbacks
  const onChangeShowCourseStandards = useCallback(value => {
    setShowCourseStandards(value);
  }, []);

  const syncWithStandardRubric = async () => {
    const _assessmentTools = !_.isEmpty(assessmentTools?.edges)
      ? assessmentTools?.edges
      : assessmentTools;
    const assessmentToolNodes = _.map(_assessmentTools, "node");
    // Doubt - @priyansh
    const standardRubric =
      _.find(assessmentToolNodes, {
        assessmentToolType: "STANDARD_BASED_RUBRIC",
      }) || {};
    const isStandardRubricPresent = !_.isEmpty(standardRubric);

    if (!isStandardRubricPresent) {
      return;
    }

    const standardRubricCriterias = _.get(
      standardRubric,
      "assessmentToolItem.standardRubricCriterias",
      []
    );

    // Find selected standards set in standards rubric
    const selectedStandardSetInRubric =
      _.get(
        _.first(standardRubricCriterias),
        "standard.standardsWrapper.standardSet.id",
        null
      ) ||
      _.get(
        _.first(standardRubricCriterias),
        "benchmark.standardsWrapper.standardSet.id",
        null
      );

    // Find selected standards in standards rubric
    const selectedStandardsInRubric = _.map(
      standardRubricCriterias,
      criteria =>
        _.get(criteria, "standard.id") || _.get(criteria, "benchmark.id")
    );

    if (selectedStandardSetInRubric) {
      const standardsEdgesNodes = _.map(
        standardsEdgesRef.current,
        ({ node }) => node
      );
      // Filter selected standards in learning goals based on the selected standard set in rubric
      // Only select standards that are graded and type individual
      const filteredSelectedStandards = _.filter(
        selectedStandards,
        standardId => {
          const standard =
            _.find(
              _.uniqBy(
                [...standardsEdgesNodes, ...previousSelectedNodes],
                "id"
              ),
              node => node?.id === standardId
            ) || {};
          const properties = _.get(standard, "standardsWrapper.properties", {});
          return (
            standard &&
            standard?.gradingType === "INDIVIDUAL" &&
            properties?.isGradedStandard &&
            _.get(standard, "standardsWrapper.standardSet.id") ===
              selectedStandardSetInRubric
          );
        }
      );

      // Find standards to add and remove in rubric
      const selectedStandardsToAddInRubric = _.difference(
        filteredSelectedStandards,
        selectedStandardsInRubric
      );
      const selectedStandardsToRemoveFromRubric = _.difference(
        selectedStandardsInRubric,
        filteredSelectedStandards
      );

      if (!_.isEmpty(selectedStandardsToRemoveFromRubric)) {
        const criteriaIdsToRemove = _.map(
          _.filter(standardRubricCriterias, criterion =>
            _.includes(
              selectedStandardsToRemoveFromRubric,
              _.get(criterion, "standard.id") ||
                _.get(criterion, "benchmark.id")
            )
          ),
          "id"
        );

        if (!_.isEmpty(criteriaIdsToRemove)) {
          await dispatch(
            removeStandardRubricCriterias({
              toolId: _.get(standardRubric, "assessmentToolItem.id"),
              type: "STANDARD_BASED_RUBRIC",
              ids: criteriaIdsToRemove,
            })
          );
        }
      }

      if (!_.isEmpty(selectedStandardsToAddInRubric)) {
        await dispatch(
          bulkCreateStandardRubricCriteria({
            input: _.map(selectedStandardsToAddInRubric, id => ({
              elementId: id,
              gradeScaleId: _.get(
                standardRubric,
                "assessmentToolItem.gradeScale.id"
              ),
              toolId: _.get(standardRubric, "assessmentToolItem.id"),
            })),
            type: "STANDARD_BASED_RUBRIC",
          })
        );
      }
    }
  };

  const handleUpdateValue = () => {
    const extraParams = {
      nodes: _.uniqBy(
        [
          ..._.map(standardsEdgesRef?.current, ({ node }) => node),
          ...previousSelectedNodes,
        ],
        "id"
      ),
      rootNodeId: "",
    };

    updateInputField(
      { [fieldKey]: selectedStandards },
      extraParams,
      true,
      hasAddRemoveValues
    );

    syncWithStandardRubric();
    onCloseLearningGoalsModal();
  };

  const handleCancelButtonClick = e => {
    e?.stopPropagation();

    switch (mode) {
      case "add":
        if (totalSelected > 0) {
          setIsDiscardModalVisible(true);
          return;
        }
        onCloseLearningGoalsModal();
        return;
      default: {
        if (newSelectionsCount > 0) {
          setIsDiscardModalVisible(true);
          return;
        }
        onCloseLearningGoalsModal();
      }
    }
  };

  // render functions
  const renderDiscardModal = () => {
    return (
      <DiscardLearningGoalsModal
        headerTitle={discardModalObj[mode].headerTitle}
        message={discardModalObj[mode].message}
        buttonLabel1={discardModalObj[mode].buttonLabel1}
        buttonLabel2={discardModalObj[mode].buttonLabel2}
        onClickButton1={handleUpdateValue}
        onClickButton2={onCloseLearningGoalsModal}
        onCloseDiscardModal={() => setIsDiscardModalVisible(false)}
      />
    );
  };

  const renderHeader = useCallback(() => {
    const onChangeShowCourseStandards = e => {
      const isChecked = _.get(e, "target.checked", false);
      setShowCourseStandards(isChecked);
    };

    const onChangeShowOnlyUnitSelections = e => {
      const isChecked = _.get(e, "target.checked", false);
      setShowOnlyUnitSelections(isChecked);
    };

    return (
      <header className={classes.headerContainer}>
        <h6 style={{ display: "inline" }}>{standardsLabel}</h6>
        <div className={classes.toggleContainer}>
          {/* TODO: Uncomment once global standards support is added */}
          {/* {learningCourseId ? (
            <div
              className={classNames(classes.contentSpaceBetween, classes.gap)}
            >
              <span className={classes.divider} />

              <span className={classNames("text-body", classes.textSecondary)}>
                {t("classRoom:only_show_standards_added_to_the_course")}
              </span>

              <ToggleSwitch
                size={"medium"}
                checked={showCourseStandards}
                onChange={onChangeShowCourseStandards}
              />
            </div>
          ) : null} */}

          {!_.isEmpty(unitPlan) ? (
            <div
              className={classNames(classes.contentSpaceBetween, classes.gap)}
            >
              <span className={classNames("text-body", classes.textSecondary)}>
                {t("classRoom:only_show_label_selected_in_this_unit", {
                  label: t("common:standard_other"),
                })}
              </span>
              <ToggleSwitch
                size={"medium"}
                checked={showOnlyUnitSelections}
                onChange={onChangeShowOnlyUnitSelections}
              />
            </div>
          ) : null}
        </div>
      </header>
    );
  }, [
    showCourseStandards,
    showOnlyUnitSelections,
    learningCourseId,
    standardsLabel,
  ]);

  return (
    <DSProvider dsVersion="2.0">
      <Modal height="80vh" width="80vw" onClose={onCloseLearningGoalsModal}>
        <Modal.Header isClosable hasSeparator style={styles.modalHeaderStyle}>
          {renderHeader()}
        </Modal.Header>
        <Modal.Body isScrollable={false} style={styles.bodyContainer}>
          <LearningGoalsStandardsTagModalBodyWithQuery
            {...props}
            showCourseStandards={showCourseStandards}
            setShowCourseStandards={onChangeShowCourseStandards}
            showOnlyUnitSelections={showOnlyUnitSelections}
            selectedStandardSet={selectedStandardSet}
            setSelectedStandardSet={setSelectedStandardSet}
            selectedStandards={selectedStandards}
            setSelectedStandards={setSelectedStandards}
            standardsEdgesRef={standardsEdgesRef}
            unitPlan={unitPlan}
          />
        </Modal.Body>

        <Modal.Footer hasSeparator>
          <footer className={classes.contentSpaceBetween}>
            <span className={standardsCountClass}>
              {areStandardsSelected
                ? t("common:standards_selected_with_count", {
                    count: _.size(selectedStandards),
                  })
                : null}
            </span>

            <div className={classes.footerRight}>
              <Button
                size={"medium"}
                onClick={handleCancelButtonClick}
                variant="neutral"
                type={"outlined"}
              >
                {t("common:cancel")}
              </Button>

              <Button
                size={"medium"}
                onClick={handleUpdateValue}
                variant={"progressive"}
                type={"fill"}
                disabled={_.isEmpty(selectedStandards)}
              >
                {t("common:select")}
              </Button>
            </div>
          </footer>
        </Modal.Footer>
        {isDiscardModalVisible ? renderDiscardModal() : null}
      </Modal>
    </DSProvider>
  );
};

const mapStateToPropsForModal = state => ({
  plannerElementSets: _.get(state, "platform.currentPlannerElementSets", {}),
});

export default connect(
  mapStateToPropsForModal,
  null
)(React.memo(LearningGoalsStandardsTagModal));
